import DiscountIcon from '@mui/icons-material/Discount';
import PeopleIcon from '@mui/icons-material/People';
import PercentIcon from '@mui/icons-material/Percent';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, alpha, Box, Stack, Theme, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import MediumMaterialUISwitch from '../../../components/common/MediumMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import SextforceMetricsPromoCreatePromoDialog from '../../../components/services/sextforce/autoPromo/SextforceMetricsPromoCreatePromoDialog';
import useSextforceMetricsAutoPromotions, { AutoPromotionSettings } from '../../../hooks/useSextforceMetricsAutoPromotions';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

const PromoDetails = ({ promoSettings }: { promoSettings: AutoPromotionSettings }) => {
    return (
        <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
            <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <DiscountIcon fontSize="small" />
                    <Typography variant="body2">{promoSettings.discount === 100 ? 'Free Trial' : 'First Month Discount'}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PeopleIcon fontSize="small" />
                    <Typography variant="body2">
                        {promoSettings.subscribeCounts} new subscriber{promoSettings.subscribeCounts !== 1 ? 's' : ''} within{' '}
                        {promoSettings.subscribeDays} day{promoSettings.subscribeDays !== 1 ? 's' : ''}
                    </Typography>
                </Stack>
                {promoSettings.discount !== 100 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PercentIcon fontSize="small" />
                        <Typography variant="body2">
                            {promoSettings.discount}% off for {promoSettings.finishDays} day
                            {promoSettings.finishDays !== 1 ? 's' : ''}
                        </Typography>
                    </Stack>
                )}
                {promoSettings.discount === 100 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TimelapseIcon fontSize="small" />
                        <Typography variant="body2">
                            Free trial for {promoSettings.finishDays} day{promoSettings.finishDays !== 1 ? 's' : ''}
                        </Typography>
                    </Stack>
                )}
            </Stack>
            <Box
                sx={{
                    bgcolor: theme => alpha(theme.palette.primary.light, 0.1),
                    padding: 1,
                    borderRadius: 1,
                }}
            >
                <Typography>{promoSettings.message}</Typography>
            </Box>
        </Stack>
    );
};

const SextforceAutoPromo = () => {
    const settingsContext = useContext(SettingsContext);
    const defaultSettings: AutoPromotionSettings = {
        onlyfansId: 0,
        type: 'none',
        discount: 100,
        message: '',
        finishDays: 7,
        subscribeCounts: 10,
        subscribeDays: 7,
        repeat: false,
    };
    const theme: Theme = useTheme();
    const [newSubsOpen, setNewSubsOpen] = useState<boolean>(false);
    const [expiredSubsOpen, setExpiredSubsOpen] = useState<boolean>(false);
    const [newSubSettings, setNewSubSettings] = useState<AutoPromotionSettings | null>(defaultSettings);
    const [expiredSubSettings, setExpiredSubSettings] = useState<AutoPromotionSettings | null>(defaultSettings);

    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();
    const sextforceActive = isSextforceActive(subscriber);

    const {
        setAutoPromoSettingRepeatOption,
        getCurrentNewSubscriberPromotionSettings,
        getCurrentExpiredSubscriberPromotionSettings,
        isCreatePromoLoading,
        isPromoSettingsLoading,
    } = useSextforceMetricsAutoPromotions();

    useEffect(() => {
        const getSettings = async () => {
            const newFanSettingsAsync = getCurrentNewSubscriberPromotionSettings();
            const expiredSettings = await getCurrentExpiredSubscriberPromotionSettings();
            const newFanSettings = await newFanSettingsAsync;

            setNewSubSettings(newFanSettings);
            setExpiredSubSettings(expiredSettings);
        };
        getSettings();
    }, [subscriber]);

    const switchNewSubsRepeatOption = async () => {
        const newSetting = await setAutoPromoSettingRepeatOption('all', !newSubSettings!.repeat);
        setNewSubSettings(newSetting);
    };
    const switchExpiredSubsRepeatOption = async () => {
        const newSetting = await setAutoPromoSettingRepeatOption('expired', !expiredSubSettings!.repeat);
        setExpiredSubSettings(newSetting);
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} /> Auto
                        Promo for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Promo" />
                    </StyledCard>

                    <StyledCard>
                        <Typography gutterBottom>
                            Set up <strong>auto-repeating OnlyFans promotions</strong> for new and expired subs. Each deal refreshes as it
                            fills, maintaining scarcity and FOMO so fans are far more likely to subscribe on the spot. Limited discounts.
                            Real urgency. More paid subs.
                        </Typography>
                        <Typography>
                            <span
                                style={{
                                    fontStyle: 'italic',
                                }}
                            >
                                Limited discounts. Real urgency. More paid subs.
                            </span>
                        </Typography>
                        {subscriber && subscriber.ofSubscriptionPrice === 0 && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                This feature only works on PAID OnlyFans pages
                            </Alert>
                        )}
                        {subscriber && subscriber.ofSubscriptionPrice !== 0 && !sextforceActive && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Sextforce service must be active to use this feature
                            </Alert>
                        )}
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title="New Subscribers"
                            badge={
                                <MediumMaterialUISwitch
                                    checked={newSubSettings?.repeat ?? false}
                                    disabled={
                                        isSubscriberLoading || (subscriber && subscriber.ofSubscriptionPrice == 0) || !sextforceActive
                                    }
                                    onChange={() => {
                                        switchNewSubsRepeatOption();
                                    }}
                                />
                            }
                        />
                    </StyledCard>
                    <StyledCard>
                        {newSubSettings && newSubSettings.type !== 'none' ? (
                            <PromoDetails promoSettings={newSubSettings} />
                        ) : (
                            <Typography sx={{ mb: 2 }}>No Settings Currently Setup</Typography>
                        )}
                        <LoadingButton
                            loading={isPromoSettingsLoading || isCreatePromoLoading}
                            onClick={() => {
                                setNewSubsOpen(true);
                            }}
                            variant="contained"
                            color="secondary"
                            size="medium"
                            fullWidth
                            disabled={isSubscriberLoading || (subscriber && subscriber.ofSubscriptionPrice == 0) || !sextforceActive}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <span>Manage Promotion</span>
                        </LoadingButton>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title="Expired Subscribers"
                            badge={
                                <MediumMaterialUISwitch
                                    checked={expiredSubSettings?.repeat ?? false}
                                    disabled={
                                        isSubscriberLoading || (subscriber && subscriber.ofSubscriptionPrice == 0) || !sextforceActive
                                    }
                                    onChange={() => {
                                        switchExpiredSubsRepeatOption();
                                    }}
                                />
                            }
                        />
                    </StyledCard>
                    <StyledCard>
                        {expiredSubSettings && expiredSubSettings.type !== 'none' ? (
                            <PromoDetails promoSettings={expiredSubSettings} />
                        ) : (
                            <Typography sx={{ mb: 2 }}>No Settings Currently Setup</Typography>
                        )}
                        <LoadingButton
                            loading={isPromoSettingsLoading || isCreatePromoLoading}
                            onClick={() => {
                                setExpiredSubsOpen(true);
                            }}
                            variant="contained"
                            color="secondary"
                            size="medium"
                            fullWidth
                            disabled={isSubscriberLoading || (subscriber && subscriber.ofSubscriptionPrice == 0) || !sextforceActive}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <span>Manage Promotion</span>
                        </LoadingButton>
                    </StyledCard>
                    {newSubsOpen && (
                        <SextforceMetricsPromoCreatePromoDialog
                            open={newSubsOpen}
                            onClose={() => setNewSubsOpen(false)}
                            audienceType="all"
                            settings={newSubSettings ?? null}
                        />
                    )}
                    {expiredSubsOpen && (
                        <SextforceMetricsPromoCreatePromoDialog
                            open={expiredSubsOpen}
                            onClose={() => setExpiredSubsOpen(false)}
                            audienceType="expired"
                            settings={expiredSubSettings ?? null}
                        />
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default SextforceAutoPromo;
